import { useState, useEffect, useRef, type FormEvent, type ChangeEvent } from "react";
import Select from "@/components/base/Select";
import Badge, { type BadgeTone } from "@/components/base/Badge";
import { useProvider } from "@/hooks/useProvider";
import { supabase } from "@/lib/supabase";
import { fetchTickets } from "@/lib/data";
import { issueTypes, tickets as mockTickets } from "@/mocks/support";
import type { SupportTicket } from "@/mocks/support";
import { Link } from "react-router-dom";

const typeOptions = issueTypes.map((t) => ({ value: t, label: t }));

const statusTone: Record<SupportTicket["status"], BadgeTone> = {
  solved: "green",
  review: "amber",
  open: "blue",
};

const statusIcon: Record<SupportTicket["status"], string> = {
  solved: "ri-check-double-line",
  review: "ri-time-line",
  open: "ri-message-3-line",
};

const statusLabelShort: Record<SupportTicket["status"], string> = {
  solved: "تم الحل",
  review: "قيد المراجعة",
  open: "مفتوحة",
};

export default function Support() {
  const { provider, loading: providerLoading } = useProvider();
  const [activeTab, setActiveTab] = useState<"new" | "previous">("new");
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);

  const [type, setType] = useState("طلب");
  const [description, setDescription] = useState("");
  const [charCount, setCharCount] = useState(0);
  const [attachmentName, setAttachmentName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = () => {
    setLoading(true);
    if (!provider) {
      setTickets(mockTickets);
      setLoading(false);
      return;
    }
    fetchTickets(provider.id)
      .then(setTickets)
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider]);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setAttachmentName(file.name);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!description.trim()) {
      setFormError("اكتب وصف المشكلة قبل الإرسال.");
      return;
    }
    setFormError("");
    setSubmitting(true);

    const ticketNumber = `TK-${String(Date.now()).slice(-4)}`;

    if (!provider) {
      setTickets((prev) => [
        { id: ticketNumber, type, date: "اليوم", status: "open", statusLabel: "مفتوحة" },
        ...prev,
      ]);
      setSubmitting(false);
      setDescription("");
      setCharCount(0);
      setAttachmentName("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      setFormSuccess(true);
      setActiveTab("previous");
      setTimeout(() => setFormSuccess(false), 3000);
      return;
    }

    const { data, error } = await supabase
      .from("support_tickets")
      .insert({
        provider_id: provider.id,
        ticket_number: ticketNumber,
        type,
        description: description.trim(),
        status: "open",
        status_label: "مفتوحة",
      })
      .select()
      .single();

    setSubmitting(false);

    if (error || !data) {
      setFormError("تعذر إرسال التذكرة، حاول مرة أخرى.");
      return;
    }

    setTickets((prev) => [
      {
        id: data.ticket_number,
        type: data.type,
        date: "اليوم",
        status: data.status as SupportTicket["status"],
        statusLabel: data.status_label,
      },
      ...prev,
    ]);
    setDescription("");
    setCharCount(0);
    setAttachmentName("");
    if (fileInputRef.current) fileInputRef.current.value = "";
    setFormSuccess(true);
    setActiveTab("previous");
    setTimeout(() => setFormSuccess(false), 3000);
  };

  const inputClass =
    "w-full rounded-[14px] border border-[#E4EFEF] bg-[#F0FDFD] px-4 py-3 text-sm text-ghasla-text-heading outline-none transition-colors focus:border-[#0D6E6E]";
  const labelClass = "mb-2 block text-sm font-medium text-ghasla-text-primary";

  if (providerLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#E4EFEF] border-t-[#0D6E6E]" />
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-ghasla-text-heading md:text-[28px]">الدعم</h1>

      {/* Tabs */}
      <div className="mt-6 flex rounded-2xl bg-white p-1.5 shadow-card">
        <button
          type="button"
          onClick={() => setActiveTab("new")}
          className={`relative flex-1 cursor-pointer rounded-[14px] py-3 text-sm font-semibold transition-all ${
            activeTab === "new"
              ? "bg-gradient-to-l from-[#0D6E6E] to-[#1A9E96] text-white shadow"
              : "text-ghasla-text-primary hover:text-ghasla-text-heading"
          }`}
        >
          <i className="ri-add-line ml-1.5" />
          إرسال تذكرة
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("previous")}
          className={`relative flex-1 cursor-pointer rounded-[14px] py-3 text-sm font-semibold transition-all ${
            activeTab === "previous"
              ? "bg-gradient-to-l from-[#0D6E6E] to-[#1A9E96] text-white shadow"
              : "text-ghasla-text-primary hover:text-ghasla-text-heading"
          }`}
        >
          <i className="ri-archive-line ml-1.5" />
          التذاكر السابقة
          {tickets.length > 0 && (
            <span className="mr-1.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#00AAB7]/15 px-1.5 text-[11px] font-bold text-[#00AAB7]">
              {tickets.length}
            </span>
          )}
        </button>
      </div>

      {/* New Ticket */}
      {activeTab === "new" && (
        <section className="mt-5 rounded-[20px] bg-white p-4 shadow-card md:p-6">
          <form onSubmit={handleSubmit}>
            <div>
              <label className={labelClass}>نوع المشكلة</label>
              <Select
                value={type}
                onChange={setType}
                options={typeOptions}
              />
            </div>

            <div className="mt-4">
              <label className={labelClass}>الوصف</label>
              <textarea
                className="min-h-[120px] w-full rounded-[14px] border border-[#E4EFEF] bg-[#F0FDFD] px-4 py-3 text-sm text-ghasla-text-heading outline-none transition-colors focus:border-[#0D6E6E]"
                value={description}
                maxLength={500}
                onChange={(e) => {
                  setDescription(e.target.value);
                  setCharCount(e.target.value.length);
                }}
                placeholder="اشرح المشكلة بالتفصيل..."
              />
              <div className="mt-1 text-left text-xs text-ghasla-text-secondary">{charCount}/500</div>
            </div>

            <div className="mt-4">
              <label className={labelClass}>مرفق (اختياري)</label>
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileChange}
                className="hidden"
              />
              {attachmentName ? (
                <div className="flex items-center justify-between rounded-[14px] border border-[#E4EFEF] bg-[#F0FDFD] px-4 py-3">
                  <span className="flex min-w-0 items-center gap-2 text-sm text-ghasla-text-heading">
                    <i className="ri-file-3-line shrink-0 text-ghasla-text-link" />
                    <span className="truncate">{attachmentName}</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setAttachmentName("");
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                    className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-full text-ghasla-text-secondary transition-colors hover:bg-[#E4EFEF] hover:text-ghasla-text-link"
                    aria-label="إزالة المرفق"
                  >
                    <i className="ri-close-line" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-[14px] border border-dashed border-[#BFDADA] bg-[#F0FDFD] px-4 py-3 text-sm font-medium text-ghasla-text-primary transition-colors hover:border-[#0D6E6E] hover:text-ghasla-text-link"
                >
                  <i className="ri-upload-2-line" />
                  إرفاق ملف
                </button>
              )}
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="mt-5 w-full cursor-pointer rounded-xl bg-gradient-to-l from-[#0D6E6E] to-[#1A9E96] px-4 py-3.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {submitting ? "جارٍ الإرسال..." : "إرسال التذكرة"}
            </button>

            {formSuccess && (
              <p className="mt-3 text-sm font-medium text-[#27AE60]">
                تم إرسال التذكرة بنجاح — بنرد عليك قريباً
              </p>
            )}
            {formError && <p className="mt-3 text-sm font-medium text-[#E74C3C]">{formError}</p>}
          </form>

          <div className="mt-5 rounded-xl bg-[#F0FDFD] p-3 text-xs text-ghasla-text-secondary">
            ملاحظة: طلبات «بنر أو رفع ظهور» تُدار خارج النظام وتتم مراجعتها من فريق الإدارة.
          </div>
        </section>
      )}

      {/* Previous Tickets */}
      {activeTab === "previous" && (
        <section className="mt-5">
          {loading ? (
            <div className="flex h-48 items-center justify-center">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#E4EFEF] border-t-[#0D6E6E]" />
            </div>
          ) : tickets.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-[20px] bg-white py-16 shadow-card">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#F0FDFD]">
                <i className="ri-archive-line text-2xl text-ghasla-text-link" />
              </div>
              <p className="mt-4 text-sm font-medium text-ghasla-text-heading">لا توجد تذاكر بعد</p>
              <p className="mt-1 text-xs text-ghasla-text-secondary">أرسلي تذكرة جديدة وستظهر هنا</p>
              <button
                type="button"
                onClick={() => setActiveTab("new")}
                className="mt-4 cursor-pointer rounded-xl bg-gradient-to-l from-[#0D6E6E] to-[#1A9E96] px-6 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
              >
                إرسال تذكرة
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {tickets.map((t) => (
                <Link
                  key={t.id}
                  to={`/provider/support/${t.id}`}
                  className="block rounded-[20px] bg-white p-5 shadow-card transition-shadow hover:shadow-card-hover"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F0FDFD]">
                        <i className={`${statusIcon[t.status]} text-lg text-ghasla-text-link`} />
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-ghasla-text-heading" dir="ltr">
                          {t.id}
                        </p>
                        <p className="text-xs text-ghasla-text-secondary">{t.type}</p>
                      </div>
                    </div>
                    <Badge tone={statusTone[t.status]}>{t.statusLabel}</Badge>
                  </div>
                  <div className="mt-4 flex items-center justify-between border-t border-[#F4F8F8] pt-3">
                    <span className="flex items-center gap-1.5 text-xs text-ghasla-text-secondary">
                      <i className="ri-calendar-line" />
                      {t.date}
                    </span>
                    <span className="flex items-center gap-1 text-xs font-medium text-ghasla-text-link">
                      عرض التفاصيل
                      <i className="ri-arrow-left-s-line" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}